import { NumberTypeEnum, useCheckRegulatoryComplianceLazyQuery } from '../../../../../../types/generated/graphql';
import { deriveRegulatoryComplianceFieldRequirements } from '../../../purchase-number/utils/regulatory-compliance-fields';

export type BundleStatusForClaim = {
  isApproved: boolean;
  isIdentityFieldRequired: boolean;
  isAddressFieldRequired: boolean;
};

type CheckBundleStatusArgs = {
  isoCountry: string;
  numberType: NumberTypeEnum;
  addressRequirements: string;
};

export function useBundleStatusForClaim() {
  const [checkRegulatoryCompliance] = useCheckRegulatoryComplianceLazyQuery({ fetchPolicy: 'network-only' });

  const checkBundleStatus = async ({
    isoCountry,
    numberType,
    addressRequirements,
  }: CheckBundleStatusArgs): Promise<BundleStatusForClaim> => {
    try {
      const { data, error } = await checkRegulatoryCompliance({
        variables: { isoCountry, numberType, addressRequirements },
      });

      // useLazyQuery's execute function resolves (rather than rejects) on a network/GraphQL
      // error by default, so a thrown error alone isn't enough — check the result too.
      if (error) {
        return { isApproved: false, isIdentityFieldRequired: true, isAddressFieldRequired: true };
      }

      const { isIdentityFieldRequired, isAddressFieldRequired, isInfoVisible } =
        deriveRegulatoryComplianceFieldRequirements(data?.checkRegulatoryCompliance?.data);

      return { isApproved: !isInfoVisible, isIdentityFieldRequired, isAddressFieldRequired };
    } catch {
      // Belt-and-suspenders: treat as unapproved (safe default: forces verification
      // rather than silently allowing an unverified claim through).
      return { isApproved: false, isIdentityFieldRequired: true, isAddressFieldRequired: true };
    }
  };

  return { checkBundleStatus };
}
